import * as React from "react";
import { XIcon } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { createBase64Image, defaultUserAvatar } from "@/helpers/images";
import { useToastDefault } from "@/hooks/use-toast-default";
import { emptyUserData, useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { useGetUserQuery } from "@/api/users-admin";
import { SnapshotCollectionPermission } from "@/modules/snapshots/snapshotCollection.types";
import { useDeleteSnapshotCollectionPermissionMutation } from "../../snapshotCollection.api";

interface Props {
  permission: SnapshotCollectionPermission;
  isReadOnly: boolean;
}

export const SnapshotCollectionShareModalUsersRow: React.FC<Props> = ({ permission, isReadOnly }) => {
  const { data: user = emptyUserData } = useGetUserQuery(permission.user_id);
  const authenticatedUser = useAuthenticatedUser();

  const { showToast } = useToastDefault();

  const [deleteSnapshotCollectionPermission] = useDeleteSnapshotCollectionPermissionMutation();

  const handleRemove = async () => {
    if (isReadOnly) {
      return;
    }
    await deleteSnapshotCollectionPermission({
      id: permission.snapshot_collection_id,
      permissionId: permission.id,
    });
    showToast("Permissions updated", "Permissions has been updated successfully.");
  };

  return (
    <div key={user.id} className="mb-3 flex items-center py-1">
      <Avatar className="mr-2 h-6 w-6">
        <AvatarImage
          src={user.avatar ? createBase64Image(user.avatar, "png") : defaultUserAvatar}
          alt={user.username}
        />
        <AvatarFallback>{user.username?.[0]?.toUpperCase() || "U"}</AvatarFallback>
      </Avatar>
      <div className="text-sm">
        {user.name} {user.surname} ({user.email})
      </div>
      <div className="ml-auto flex items-center text-sm text-gray-600">
        <span className="capitalize">{permission.role}</span>{" "}
        {authenticatedUser.id !== permission.user_id && !isReadOnly ? (
          <XIcon className="ml-2 h-4 w-4 cursor-pointer text-gray-600" onClick={handleRemove} />
        ) : (
          <XIcon className="ml-2 h-4 w-4 text-gray-300" />
        )}
      </div>
    </div>
  );
};
